import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  DAY_MINUTES,
  MINUTE,
  endOf,
  isAllDay,
  layoutDay,
  minuteOfDay,
  sameDay,
  snap,
  startOfDay,
} from "../../lib/calendar";
import {
  TONE_CLASSES,
  afterState,
  meetingTitle,
  toneOf,
} from "../../lib/meetings-view";
import type { SalesMeeting } from "../../lib/sales-types";
import { cn } from "../ui/cn";
import { timeRange } from "./MeetingRow";
import { AfterIcon } from "./AfterIcon";

/**
 * The day and week views: hours down the side, days across, meetings as blocks.
 *
 * Copies Google Calendar's grid on purpose, interactions included, because the
 * reps already know it and a diary is the last place anybody wants to learn a
 * new gesture:
 *
 * - **Click an empty slot** to book an hour from there; **drag** down an empty
 *   column to book exactly the stretch you dragged.
 * - **Drag a meeting** to move it, across days as well as hours, and drag its
 *   bottom edge to make it longer or shorter. Both snap to quarter-hours.
 * - **Click a meeting** to open it beside the calendar.
 *
 * On a touchscreen a drag starts with **press and hold**, as in Google's app:
 * the same finger movement is a scroll, so moving straight away scrolls, and
 * holding still for half a second picks the meeting (or an empty slot) up.
 * A tap still opens a meeting or books an hour.
 *
 * A move is shown in its new place the moment it is dropped and saved behind
 * it. If the server refuses -- somebody else's meeting, a clash with the rules
 * -- it springs back and the caller says why.
 */

const HOUR_PX = 48;
const PX_PER_MINUTE = HOUR_PX / 60;
/** How far a pointer must travel before a press becomes a drag. */
const DRAG_THRESHOLD = 4;
/** How long a finger must stay put to pick a meeting up, as in Google's app. */
const HOLD_MS = 450;
/** How far it may wander in that time and still count as holding. */
const HOLD_SLOP = 8;

interface Props {
  days: Date[];
  meetings: SalesMeeting[];
  now: Date;
  selectedId: number | null;
  canDrag: (meeting: SalesMeeting) => boolean;
  /** With the block's box, so a card can open beside it. */
  onOpen: (meeting: SalesMeeting, anchor: DOMRect) => void;
  onCreate: (start: Date, minutes: number) => void;
  onMove: (meeting: SalesMeeting, start: Date, end: Date) => Promise<unknown>;
  /** A day heading was pressed: open that day on its own. */
  onPickDay: (day: Date) => void;
}

type Drag =
  | {
      kind: "create";
      x: number;
      y: number;
      day: number;
      from: number;
      to: number;
      moved: boolean;
    }
  | {
      kind: "move" | "resize";
      meeting: SalesMeeting;
      x: number;
      y: number;
      /** Where the pointer sat inside the block, so it does not jump. */
      grab: number;
      day: number;
      start: number;
      duration: number;
      moved: boolean;
    };

export function TimeGrid({
  days,
  meetings,
  now,
  selectedId,
  canDrag,
  onOpen,
  onCreate,
  onMove,
  onPickDay,
}: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  /* Set after a drag so the click that follows the pointerup does not also
     open the meeting that was just moved. */
  const swallowClick = useRef(false);
  const lastPointer = useRef<string>("mouse");
  /* A touch held still, waiting to become a drag. See `start`. */
  const hold = useRef<{ x: number; y: number; timer: number } | null>(null);
  const [preview, setPreview] = useState<Drag | null>(null);
  /* Moves dropped but not yet confirmed, drawn where they were dropped. */
  const [pending, setPending] = useState<Map<number, { startAt: string; endAt: string }>>(
    () => new Map(),
  );

  const shown = useMemo(
    () =>
      meetings.map((meeting) => {
        const moved = pending.get(meeting.id);
        return moved ? { ...meeting, ...moved } : meeting;
      }),
    [meetings, pending],
  );

  const timed = shown.filter((meeting) => !isAllDay(meeting));
  const allDay = shown.filter(isAllDay);

  /*
   * The hours on show: 07:00 to 20:00, the working day, stretched to take in
   * anything booked outside it so an early start is never off the top.
   */
  const [firstHour, lastHour] = useMemo(() => {
    let first = 7;
    let last = 20;
    for (const meeting of timed) {
      const start = new Date(meeting.startAt);
      if (!days.some((day) => sameDay(day, start))) continue;
      first = Math.min(first, start.getHours());
      const end = endOf(meeting);
      const endHour = sameDay(end, start) ? Math.ceil(minuteOfDay(end) / 60) : 24;
      last = Math.max(last, endHour);
    }
    return [first, Math.min(24, last)];
  }, [timed, days]);

  const offset = firstHour * 60;
  const height = (lastHour - firstHour) * HOUR_PX;

  /* Open on the current time, as Google does, or the start of the day. */
  const anchor = days[0]!.getTime();
  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const showingToday = days.some((day) => sameDay(day, new Date()));
    const target = showingToday ? (minuteOfDay(new Date()) - offset - 90) * PX_PER_MINUTE : 0;
    element.scrollTop = Math.max(0, target);
    // Only when the range changes -- not every time a meeting is saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, days.length]);

  /*
   * Once a finger has picked something up, the grid must stop scrolling under
   * it. Pointer events cannot cancel a scroll; a non-passive touchmove can,
   * and React only attaches passive ones -- so this is a native listener.
   */
  useEffect(() => {
    const element = grid.current;
    if (!element) return;
    const onTouchMove = (event: TouchEvent): void => {
      if (drag.current) event.preventDefault();
    };
    element.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      element.removeEventListener("touchmove", onTouchMove);
      if (hold.current) window.clearTimeout(hold.current.timer);
    };
  }, []);

  useEffect(() => {
    /* A refetch has landed: whatever it says is the truth now. */
    setPending(new Map());
  }, [meetings]);

  /** The day column and minute of day under a pointer. */
  const locate = (clientX: number, clientY: number): { day: number; minute: number } => {
    const rect = grid.current!.getBoundingClientRect();
    const column = Math.floor(((clientX - rect.left) / rect.width) * days.length);
    const minute = (clientY - rect.top) / PX_PER_MINUTE + offset;
    return {
      day: Math.min(days.length - 1, Math.max(0, column)),
      minute: Math.min(DAY_MINUTES, Math.max(0, minute)),
    };
  };

  const begin = (pointerId: number, next: Drag): void => {
    drag.current = next;
    try {
      grid.current?.setPointerCapture(pointerId);
    } catch {
      /* The pointer has already gone -- a hold that ended as it fired. */
    }
  };

  const cancelHold = (): void => {
    if (hold.current) window.clearTimeout(hold.current.timer);
    hold.current = null;
  };

  /**
   * Start a drag: straight away with a mouse, after a press-and-hold on a
   * touchscreen.
   *
   * The hold is what lets one finger do both jobs, as in Google's app. Moving
   * straight away is a scroll, and the timer is abandoned; staying put for
   * half a second picks the meeting up -- with a buzz where the phone has one
   * -- and from then on the finger drags it and the grid stops scrolling.
   */
  const start = (event: React.PointerEvent, next: Drag): void => {
    if (event.pointerType !== "touch") {
      begin(event.pointerId, next);
      return;
    }
    cancelHold();
    const pointerId = event.pointerId;
    hold.current = {
      x: event.clientX,
      y: event.clientY,
      timer: window.setTimeout(() => {
        hold.current = null;
        navigator.vibrate?.(15);
        /* Lifted: drawn as a drag already, so it is visibly picked up. A
           hold on an empty slot starts as an hour, as a tap would book. */
        const lifted: Drag =
          next.kind === "create"
            ? { ...next, from: Math.floor(next.from / 30) * 30, to: Math.floor(next.from / 30) * 30 + 60, moved: true }
            : { ...next, moved: true };
        begin(pointerId, lifted);
        setPreview(lifted);
        /* Whatever happens next, the tap that would follow is not a tap. */
        swallowClick.current = true;
      }, HOLD_MS),
    };
  };

  const onGridPointerDown = (event: React.PointerEvent): void => {
    lastPointer.current = event.pointerType;
    if (event.pointerType !== "touch" && event.button !== 0) return;
    const { day, minute } = locate(event.clientX, event.clientY);
    const from = Math.floor(minute / 15) * 15;
    start(event, {
      kind: "create",
      x: event.clientX,
      y: event.clientY,
      day,
      from,
      to: from + 15,
      moved: false,
    });
  };

  const onBlockPointerDown = (
    event: React.PointerEvent,
    meeting: SalesMeeting,
    kind: "move" | "resize",
  ): void => {
    lastPointer.current = event.pointerType;
    event.stopPropagation();
    if (event.pointerType !== "touch" && event.button !== 0) return;
    if (!canDrag(meeting)) return;

    const begins = new Date(meeting.startAt);
    const { minute } = locate(event.clientX, event.clientY);
    start(event, {
      kind,
      meeting,
      x: event.clientX,
      y: event.clientY,
      grab: minute - minuteOfDay(begins),
      day: days.findIndex((candidate) => sameDay(candidate, begins)),
      start: minuteOfDay(begins),
      duration: (endOf(meeting).getTime() - begins.getTime()) / MINUTE,
      moved: false,
    });
  };

  const onPointerMove = (event: React.PointerEvent): void => {
    /* A finger that moves before the hold fires is scrolling. Let it. */
    if (
      hold.current &&
      Math.hypot(event.clientX - hold.current.x, event.clientY - hold.current.y) > HOLD_SLOP
    ) {
      cancelHold();
    }

    const current = drag.current;
    if (!current) return;

    const moved =
      current.moved ||
      Math.abs(event.clientX - current.x) > DRAG_THRESHOLD ||
      Math.abs(event.clientY - current.y) > DRAG_THRESHOLD;
    if (!moved) return;

    const { day, minute } = locate(event.clientX, event.clientY);

    if (current.kind === "create") {
      const at = snap(minute);
      const anchorMinute = current.from;
      drag.current = {
        ...current,
        moved: true,
        /* Dragging upwards books from where the pointer is to where it
           started, as it does in Google. */
        to: Math.max(at, anchorMinute + 15),
        from: at < anchorMinute ? at : anchorMinute,
      };
    } else if (current.kind === "move") {
      const start = snap(minute - current.grab);
      drag.current = {
        ...current,
        moved: true,
        day,
        start: Math.min(DAY_MINUTES - 15, Math.max(0, start)),
      };
    } else {
      const end = snap(minute);
      drag.current = {
        ...current,
        moved: true,
        duration: Math.max(15, end - current.start),
      };
    }
    setPreview(drag.current);
  };

  const onPointerUp = (): void => {
    cancelHold();
    const current = drag.current;
    drag.current = null;
    setPreview(null);
    if (!current) return;

    if (current.kind === "create") {
      const day = days[current.day]!;
      if (!current.moved) {
        /* A click books an hour from the half-hour it landed in. */
        const from = Math.floor(current.from / 30) * 30;
        onCreate(atMinute(day, from), 60);
      } else {
        onCreate(atMinute(day, current.from), current.to - current.from);
      }
      swallowClick.current = true;
      return;
    }

    if (!current.moved) return; // A plain click: the block's onClick opens it.
    swallowClick.current = true;

    const day = days[Math.max(0, current.day)]!;
    const start = atMinute(day, current.start);
    const end = new Date(start.getTime() + current.duration * MINUTE);
    const was = new Date(current.meeting.startAt);
    if (start.getTime() === was.getTime() && end.getTime() === endOf(current.meeting).getTime()) {
      return;
    }

    setPending((map) =>
      new Map(map).set(current.meeting.id, {
        startAt: start.toISOString(),
        endAt: end.toISOString(),
      }),
    );
    void onMove(current.meeting, start, end).catch(() => {
      /* Refused: put it back where the server still has it. */
      setPending((map) => {
        const next = new Map(map);
        next.delete(current.meeting.id);
        return next;
      });
    });
  };

  /** A tap on an empty slot, from a touchscreen. Mouse books on pointerup. */
  const onGridClick = (event: React.MouseEvent): void => {
    if (swallowClick.current) {
      swallowClick.current = false;
      return;
    }
    if (lastPointer.current !== "touch") return;
    const { day, minute } = locate(event.clientX, event.clientY);
    onCreate(atMinute(days[day]!, Math.floor(minute / 30) * 30), 60);
  };

  /*
   * On a phone's week, splitting a fifty-pixel column between two overlapping
   * meetings leaves two slivers a letter wide. They cascade instead -- each one
   * full width, nudged right of the one under it -- which is what Google's app
   * does, and the one on top stays readable.
   */
  const cascade = useNarrow() && days.length > 1;

  const todayIndex = days.findIndex((day) => sameDay(day, now));
  const nowTop = (minuteOfDay(now) - offset) * PX_PER_MINUTE;

  return (
    /*
      One scroller holding the headings as a sticky row, rather than headings
      above a scroller: a scrollbar takes width from the grid and not from a
      row outside it, and the days would stop lining up with their columns.
    */
    <div ref={scroller} className="h-full min-h-0 overflow-y-auto">
      <div className="sticky top-0 z-30 bg-surface">
      {/* Day headings. A press opens that day on its own, as in Google. */}
      <div className="flex border-b border-line">
        <div className="w-12 shrink-0" />
        <div
          className="grid flex-1"
          style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
        >
          {days.map((day) => {
            const today = sameDay(day, now);
            return (
              <button
                key={day.getTime()}
                type="button"
                onClick={() => onPickDay(day)}
                className="flex flex-col items-center gap-0.5 py-1.5 hover:bg-surface-2"
              >
                <span
                  className={cn(
                    "text-[10px] font-medium uppercase tracking-wide",
                    today ? "text-brand-600 dark:text-brand-400" : "text-faint",
                  )}
                >
                  {day.toLocaleDateString("en-GB", { weekday: "short" })}
                </span>
                <span
                  className={cn(
                    "flex size-7 items-center justify-center rounded-full text-sm tabular-nums sm:size-8 sm:text-base",
                    today ? "bg-brand-600 font-semibold text-white" : "text-content",
                  )}
                >
                  {day.getDate()}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {allDay.length > 0 ? (
        <div className="flex border-b border-line">
          <div className="w-12 shrink-0 self-center pr-1 text-right text-[10px] text-faint">
            all day
          </div>
          <div
            className="grid flex-1 gap-px py-1"
            style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
          >
            {days.map((day) => (
              <div key={day.getTime()} className="min-w-0 space-y-px px-0.5">
                {allDay
                  .filter((meeting) => {
                    const start = startOfDay(new Date(meeting.startAt));
                    return day >= start && day < endOf(meeting);
                  })
                  .map((meeting) => (
                    <button
                      key={meeting.id}
                      type="button"
                      onClick={(event) => onOpen(meeting, event.currentTarget.getBoundingClientRect())}
                      className={cn(
                        "block w-full truncate rounded border-l-2 px-1 text-left text-[11px]",
                        TONE_CLASSES[toneOf(meeting)],
                      )}
                    >
                      {meetingTitle(meeting)}
                    </button>
                  ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      </div>

        <div className="relative flex" style={{ height }}>
          {/* The hour labels, set against the line they name. */}
          <div className="relative w-12 shrink-0">
            {Array.from({ length: lastHour - firstHour }, (_, index) => firstHour + index)
              .slice(1)
              .map((hour) => (
                <span
                  key={hour}
                  className="absolute right-1.5 -translate-y-1/2 text-[10px] tabular-nums text-faint"
                  style={{ top: (hour - firstHour) * HOUR_PX }}
                >
                  {String(hour).padStart(2, "0")}:00
                </span>
              ))}
          </div>

          <div
            ref={grid}
            onPointerDown={onGridPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => {
              cancelHold();
              drag.current = null;
              setPreview(null);
            }}
            /* A long press would otherwise open the phone's own menu. */
            onContextMenu={(event) => {
              if (lastPointer.current === "touch") event.preventDefault();
            }}
            onClick={onGridClick}
            className="relative grid flex-1 cursor-default select-none border-l border-line [-webkit-touch-callout:none]"
            style={{
              gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
              /* Hour lines, solid, with a fainter half-hour between. */
              backgroundImage: `repeating-linear-gradient(to bottom, var(--color-line) 0 1px, transparent 1px ${HOUR_PX / 2}px, color-mix(in srgb, var(--color-line) 45%, transparent) ${HOUR_PX / 2}px ${HOUR_PX / 2 + 1}px, transparent ${HOUR_PX / 2 + 1}px ${HOUR_PX}px)`,
            }}
          >
            {days.map((day, column) => (
              <div
                key={day.getTime()}
                className={cn(
                  "relative min-w-0 border-r border-line last:border-r-0",
                  column === todayIndex && "bg-brand-500/[0.03]",
                )}
              >
                {layoutDay(
                  timed.filter((meeting) => sameDay(new Date(meeting.startAt), day)),
                  day,
                ).map(({ item: meeting, top, bottom, column: lane, columns }) => {
                  const dragging =
                    preview && preview.kind !== "create" && preview.meeting.id === meeting.id;
                  const blockHeight = (bottom - top) * PX_PER_MINUTE;
                  const draggable = canDrag(meeting);
                  return (
                    <div
                      key={meeting.id}
                      role="button"
                      tabIndex={0}
                      title={tooltip(meeting)}
                      onPointerDown={(event) => onBlockPointerDown(event, meeting, "move")}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (swallowClick.current) {
                          swallowClick.current = false;
                          return;
                        }
                        onOpen(meeting, event.currentTarget.getBoundingClientRect());
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          onOpen(meeting, event.currentTarget.getBoundingClientRect());
                        }
                      }}
                      className={cn(
                        "absolute overflow-hidden rounded-md border-l-[3px] px-1 py-0.5 text-left text-[11px] leading-tight shadow-sm sm:px-1.5",
                        TONE_CLASSES[toneOf(meeting)],
                        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
                        selectedId === meeting.id && "ring-2 ring-brand-500",
                        dragging && "opacity-40",
                      )}
                      style={{
                        top: (top - offset) * PX_PER_MINUTE + 1,
                        height: Math.max(18, blockHeight - 2),
                        ...(cascade
                          ? {
                              left: `${2 + lane * 8}px`,
                              width: `calc(100% - ${4 + lane * 8}px)`,
                              zIndex: lane + 1,
                            }
                          : {
                              left: `calc(${(lane / columns) * 100}% + 2px)`,
                              width: `calc(${100 / columns}% - 4px)`,
                            }),
                      }}
                    >
                      <BlockBody
                        meeting={meeting}
                        tall={blockHeight >= 40}
                        narrow={days.length > 1}
                        now={now}
                      />
                      {draggable ? (
                        /* The bottom edge, for making it longer or shorter. */
                        <div
                          onPointerDown={(event) => onBlockPointerDown(event, meeting, "resize")}
                          className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize"
                        />
                      ) : null}
                    </div>
                  );
                })}

                {/* The red line for now, on today's column only. */}
                {column === todayIndex && nowTop >= 0 && nowTop <= height ? (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-red-500"
                    style={{ top: nowTop }}
                  >
                    <span className="absolute -left-1 -top-[3px] size-2 rounded-full bg-red-500" />
                  </div>
                ) : null}

                <DragGhost preview={preview} column={column} offset={offset} />
              </div>
            ))}
          </div>
        </div>
    </div>
  );
}

/** Whether the screen is phone-width, following it as it changes. */
function useNarrow(): boolean {
  const query = "(max-width: 639px)";
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) return;
    const onChange = (): void => setNarrow(list.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

/** What is being dragged, drawn where it would land. */
function DragGhost({
  preview,
  column,
  offset,
}: {
  preview: Drag | null;
  column: number;
  offset: number;
}) {
  if (!preview || !preview.moved || preview.day !== column) return null;

  const [from, to, label] =
    preview.kind === "create"
      ? [preview.from, preview.to, "New meeting"]
      : [preview.start, preview.start + preview.duration, meetingTitle(preview.meeting)];

  return (
    <div
      className="pointer-events-none absolute inset-x-0.5 z-20 rounded-md border-l-[3px] border-l-brand-600 bg-brand-500/25 px-1.5 py-0.5 text-[11px] font-medium text-content shadow-md"
      style={{ top: (from - offset) * PX_PER_MINUTE, height: (to - from) * PX_PER_MINUTE }}
    >
      {clockOf(from)} – {clockOf(to)}
      <div className="truncate font-normal">{label}</div>
    </div>
  );
}

function BlockBody({
  meeting,
  tall,
  narrow,
  now,
}: {
  meeting: SalesMeeting;
  tall: boolean;
  /** A week on a phone: columns about fifty pixels wide. */
  narrow: boolean;
  now: Date;
}) {
  const after = afterState(meeting, now);
  const company = meeting.crmCompanyName ?? meeting.companyName;
  const title = meeting.subject?.trim();

  /*
   * Seven columns on a phone leave about fifty pixels each, which is room for
   * a word, not for "13:00 – 14:30 Site survey Bright Bikes" truncated three
   * times. So a phone's week shows the name alone, wrapped onto as many lines
   * as the block is tall -- what Google's app does -- and the time is the
   * block's position on the grid. Wider screens get the full block below.
   */
  const phone = narrow ? (
    <div className="break-words text-[10px] font-semibold leading-tight [hyphens:auto] sm:hidden">
      {title || company}
    </div>
  ) : null;

  return (
    <>
      {phone}
      <div className={cn(narrow && "hidden sm:block")}>
      {after ? (
        <span className="absolute right-1 top-0.5">
          <AfterIcon state={after} />
        </span>
      ) : null}
      {tall ? (
        <>
          <div className="truncate pr-4 tabular-nums opacity-80">
            {timeRange(meeting.startAt, meeting.endAt)}
          </div>
          <div className="truncate pr-1 font-semibold">{title || company}</div>
          {title ? <div className="truncate opacity-80">{company}</div> : null}
        </>
      ) : (
        <div className="truncate pr-4">
          <span className="font-semibold">{title || company}</span>
          <span className="opacity-80">, {timeRange(meeting.startAt, null)}</span>
        </div>
      )}
      </div>
    </>
  );
}

/** The hover card, as plain text: when, who and with whom. */
function tooltip(meeting: SalesMeeting): string {
  const going = meeting.attendees.map((person) => person.name ?? "Somebody").join(", ");
  return [
    `${timeRange(meeting.startAt, meeting.endAt)}  ${meetingTitle(meeting)}`,
    meeting.crmCompanyName ?? meeting.companyName,
    meeting.contactName ? `Seeing ${meeting.contactName}` : null,
    going ? `Going: ${going}` : null,
    meeting.mode === "online" ? "Online" : meeting.location,
  ]
    .filter(Boolean)
    .join("\n");
}

function atMinute(day: Date, minute: number): Date {
  const start = startOfDay(day);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, minute);
}

function clockOf(minute: number): string {
  const hours = Math.floor(minute / 60) % 24;
  const minutes = minute % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

